import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { useApproveVoucher } from "@/hooks/use-voucher-actions";
import { useCreateUpsell } from "@/hooks/use-upsells";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useVoucherApprovalContext } from "@/hooks/use-voucher-approval-context";
import { VoucherBudgetPanel } from "./VoucherBudgetPanel";
import { formatNOK, formatDate } from "@/lib/format";

interface VoucherApprovalModalProps {
  /** `null` betyr "ingen bilag valgt" — dialogen forblir lukket/tom. */
  voucherId: string | null;
  projectId: string | null;
  /** Startvisning når dialogen åpnes — bruker kan bytte fritt mellom godkjenn/avvis inni dialogen. */
  mode: "approve" | "reject";
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * VIB-1226 + VIB-1204: selvforsynt godkjenningsdialog (modal). Erstatter
 * det gamle inline-panelet (`VoucherApprovalDialog.tsx`, fortsatt i bruk når
 * `feature_pm_inbox_approval` er av) med én komponent som:
 * - henter bilag + budsjettlinjer + allokeringer selv (`useVoucherApprovalContext`)
 * - viser budsjettpanelet (VIB-1204) med differanse for valgt linje
 * - støtter full godkjenn/avvis-flyt uten navigering, brukbar fra både
 *   BilagTab og PM-innboksen
 *
 * Designvalg (avgjort i bølge 3-specen, Erik kan overstyre i PR-review):
 * modal (ikke inline-ekspansjon), tomt DL-kommentarfelt (ingen forhåndsutfylling).
 */
export default function VoucherApprovalModal({
  voucherId,
  projectId,
  mode,
  open,
  onOpenChange,
}: VoucherApprovalModalProps) {
  const { activeUser } = useImpersonation();
  const approveMut = useApproveVoucher();
  const createUpsellMut = useCreateUpsell();

  const [stage, setStage] = useState<"approve" | "reject">(mode);
  const [selectedBudgetLine, setSelectedBudgetLine] = useState("");
  const [ekstraAmount, setEkstraAmount] = useState("");
  const [approveComment, setApproveComment] = useState("");
  const [rejectComment, setRejectComment] = useState("");

  const { data, isLoading } = useVoucherApprovalContext(voucherId, projectId, open);
  const voucher = data?.voucher ?? null;

  // Reset internal state hver gang dialogen åpnes for et (nytt) bilag —
  // ellers lekker forrige bilags valg inn i neste (tomt DL-kommentarfelt er
  // et bevisst designvalg, ikke bare et default-state-uhell).
  useEffect(() => {
    if (!open) return;
    setStage(mode);
    setSelectedBudgetLine("");
    setEkstraAmount("");
    setApproveComment("");
    setRejectComment("");
  }, [open, voucherId, mode]);

  const isPendingApproval = voucher?.status === "pending_approval";

  const handleApprove = async () => {
    if (!voucher || !projectId || !selectedBudgetLine || !activeUser) return;
    try {
      if (selectedBudgetLine === "__ekstra__") {
        const agreedAmount = parseFloat(ekstraAmount);
        await createUpsellMut.mutateAsync({
          project_id: projectId,
          description: voucher.supplier_name
            ? `Mersalg: ${voucher.supplier_name} – ${voucher.description ?? ""}`
            : `Mersalg fra bilag #${voucher.voucher_no}`,
          amount_excl_vat: isNaN(agreedAmount) || agreedAmount <= 0 ? (voucher.amount ?? 0) : agreedAmount,
          category: "bilag",
          source_voucher_id: voucher.id,
        });
        await approveMut.mutateAsync({
          voucher_id: voucher.id,
          decision: "approved",
          // Se VoucherApprovalDialog.tsx (gammel sti) for samme begrunnelse:
          // pending_approval krever budget_line_id, mersalg-koblingen via en
          // sentinel-id er ikke trivielt, så vi faller tilbake til legacy-flyt.
          budget_line_id: null,
          comment: approveComment.trim() || undefined,
          user_id: activeUser.id,
          project_id: projectId,
        });
      } else if (selectedBudgetLine === "__annet__") {
        await approveMut.mutateAsync({
          voucher_id: voucher.id,
          decision: "approved",
          budget_line_id: null,
          comment: approveComment.trim() || undefined,
          user_id: activeUser.id,
          project_id: projectId,
        });
      } else {
        await approveMut.mutateAsync({
          voucher_id: voucher.id,
          decision: "approved",
          budget_line_id: selectedBudgetLine,
          comment: approveComment.trim() || undefined,
          user_id: activeUser.id,
          project_id: projectId,
        });
      }
      onOpenChange(false);
    } catch (err) {
      console.error("Approval failed:", err);
    }
  };

  const handleReject = async () => {
    if (!voucher || !activeUser) return;
    if (!rejectComment.trim()) return;
    try {
      await approveMut.mutateAsync({
        voucher_id: voucher.id,
        decision: "rejected",
        comment: rejectComment.trim(),
        user_id: activeUser.id,
        project_id: projectId ?? undefined,
      });
      onOpenChange(false);
    } catch (err) {
      console.error("Rejection failed:", err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {voucher?.supplier_name ?? (voucher ? `Bilag #${voucher.voucher_no ?? ""}` : "Bilag")}
          </DialogTitle>
          <DialogDescription>
            {voucher
              ? `${formatNOK(voucher.amount)} · ${formatDate(voucher.voucher_date)}${voucher.description ? ` · ${voucher.description}` : ""}`
              : "Laster bilag …"}
          </DialogDescription>
        </DialogHeader>

        {isLoading || !voucher ? (
          <div className="flex items-center justify-center py-8">
            <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-3">
            {stage === "approve" && (
              <>
                <VoucherBudgetPanel
                  budgetLines={data?.budgetLines ?? []}
                  voucherAllocations={data?.voucherAllocations ?? []}
                  voucherAmount={voucher.amount}
                  selectedBudgetLineId={selectedBudgetLine}
                />

                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">Knytt til budsjettpost:</label>
                  <select
                    className="w-full text-sm border rounded-md px-2 py-1.5 bg-white"
                    value={selectedBudgetLine}
                    onChange={(e) => {
                      setSelectedBudgetLine(e.target.value);
                      setEkstraAmount("");
                    }}
                  >
                    <option value="">Velg budsjettlinje...</option>
                    {(data?.budgetLines ?? []).map((bl) => (
                      <option key={bl.id} value={bl.id}>
                        {bl.phase ? `${bl.phase} – ` : ""}
                        {bl.description ?? "Uten beskrivelse"} ({formatNOK(bl.amount)})
                      </option>
                    ))}
                    <option disabled>──────────</option>
                    <option value="__annet__">Annet (generell kostnad)</option>
                    <option value="__ekstra__">Ekstra (opprett mersalg)</option>
                  </select>
                </div>

                {selectedBudgetLine === "__ekstra__" && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">
                      Avtalt pris eks. mva (valgfritt, bruker bilagsbeløp om tomt):
                    </label>
                    <input
                      type="number"
                      className="w-full text-sm border rounded-md px-2 py-1.5 bg-white"
                      placeholder={`${voucher.amount}`}
                      value={ekstraAmount}
                      onChange={(e) => setEkstraAmount(e.target.value)}
                    />
                  </div>
                )}

                {isPendingApproval && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">
                      Kommentar til daglig leder (valgfritt):
                    </label>
                    <input
                      type="text"
                      className="w-full text-sm border rounded-md px-2 py-1.5 bg-white"
                      placeholder="F.eks. forklaring eller begrunnelse"
                      value={approveComment}
                      onChange={(e) => setApproveComment(e.target.value)}
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Bilaget sendes til daglig leder med navn, dato og budsjettlinje.
                    </p>
                  </div>
                )}
              </>
            )}

            {stage === "reject" && (
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground">
                  Begrunnelse{isPendingApproval ? " (påkrevd)" : ""}:
                </label>
                <input
                  type="text"
                  className="w-full text-sm border rounded-md px-2 py-1.5"
                  placeholder="Grunn for avvisning..."
                  value={rejectComment}
                  onChange={(e) => setRejectComment(e.target.value)}
                />
                {isPendingApproval && (
                  <p className="text-[11px] text-muted-foreground">
                    Bilaget returneres til regnskapsfører i Go med begrunnelsen.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-row justify-between sm:justify-between">
          {stage === "approve" ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => setStage("reject")}>
              Avvis i stedet
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={() => setStage("approve")}>
              Tilbake til godkjenning
            </Button>
          )}
          {stage === "approve" ? (
            <Button
              type="button"
              size="sm"
              className="bg-green-600 hover:bg-green-700"
              disabled={!voucher || !selectedBudgetLine || approveMut.isPending || createUpsellMut.isPending}
              onClick={handleApprove}
            >
              {approveMut.isPending ? (
                <SrIcon name="loader2" className="w-3.5 h-3.5 animate-spin mr-1.5" />
              ) : (
                <SrIcon name="check" className="w-3.5 h-3.5 mr-1.5" />
              )}
              {isPendingApproval ? "Godkjenn og send til DL" : "Godkjenn"}
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!voucher || approveMut.isPending || (isPendingApproval && !rejectComment.trim())}
              onClick={handleReject}
            >
              {approveMut.isPending ? (
                <SrIcon name="loader2" className="w-3.5 h-3.5 animate-spin mr-1.5" />
              ) : (
                <SrIcon name="x" className="w-3.5 h-3.5 mr-1.5" />
              )}
              Avvis
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
